import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { redeemApi } from '@/rest/modules/redeem-api'
import { CompleteRedemptionRequest, RedemptionFilters } from '@/domain/redeem/redeem'
import { useToast } from '../use-toast'

export const redeemKeys = {
  all: ['redemptions'] as const,
  lists: () => [...redeemKeys.all, 'list'] as const,
  list: (filters: RedemptionFilters) => [...redeemKeys.lists(), { filters }] as const,
  details: () => [...redeemKeys.all, 'detail'] as const,
  detail: (id: string) => [...redeemKeys.details(), id] as const,
  byContract: (contractId: string) => [...redeemKeys.all, 'byContract', contractId] as const,
}

export function useRedemptions(filters: RedemptionFilters = {}) {
  return useQuery({
    queryKey: redeemKeys.list(filters),
    queryFn: () => redeemApi.getRedemptions(filters),
  })
}

export function useRedemption(id: string) {
  return useQuery({
    queryKey: redeemKeys.detail(id),
    queryFn: () => redeemApi.getRedemption(id),
    enabled: !!id,
  })
}

export function useRedemptionsByContract(contractId: string) {
  return useQuery({
    queryKey: redeemKeys.byContract(contractId),
    queryFn: () => redeemApi.getRedemptionsByContract(contractId),
    enabled: !!contractId,
    staleTime: 100
  })
}

export function useCompleteRedemption() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompleteRedemptionRequest }) =>
      redeemApi.updateRedemption(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: redeemKeys.all })
      queryClient.invalidateQueries({ queryKey: ['agreements'] })
      toast({
        title: 'Éxito',
        description: 'Redención completada correctamente.',
        variant: 'default',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Error al completar la redención.',
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteRedemption() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => redeemApi.deleteRedemption(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: redeemKeys.all })
      toast({
        title: 'Éxito',
        description: 'Redención eliminada correctamente.',
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Error al eliminar la redención.',
        variant: 'destructive',
      })
    },
  })
}
